import type { ParseKeys } from 'i18next'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { TabNav } from '@/components/ui/TabNav'
import { TabPanel } from '@/components/ui/TabPanel'
import { Tag } from '@/components/ui/Tag'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { useDocumentTemplates } from '@/features/directories/api/useDirectories'
import {
  useCreateEntity,
  useIssuingEntities,
  useNumberingSchemes,
  usePreviewNumbering,
  useSetEntityActive,
  useSetNumberingScheme,
  useUpdateEntity,
} from '@/features/settings/api/useCompanySettings'
import {
  useCreateTemplate,
  useDeleteTemplate,
  useUpdateTemplate,
} from '@/features/settings/api/useCompanyTemplates'
import {
  useCreateDirection,
  useDeleteDirection,
  useDirectionTemplates,
  useSetDirectionActive,
  useUpdateDirection,
} from '@/features/settings/api/useDirectionTemplates'
import { AccessMatrixPanel } from '@/features/settings/components/AccessMatrixPanel'
import {
  DirectionFormSheet,
  type DirectionDraft,
} from '@/features/settings/components/DirectionFormSheet'
import { EntityLogoField } from '@/features/settings/components/EntityLogoField'
import { RolesPanel } from '@/features/settings/components/RolesPanel'
import { ThresholdsPanel } from '@/features/settings/components/ThresholdsPanel'
import {
  EntityFormSheet,
  NumberingFormSheet,
  TemplateFormSheet,
  type EntityDraft,
} from '@/features/settings/components/SettingsFormSheets'
import type {
  DirectionTemplate,
  DocumentTemplate,
  IssuingEntity,
  NumberingScheme,
} from '@/types/domain'

/** `null` closed, `'new'` adding, a record editing — see DirectoriesPage. */
type EditTarget<T> = T | 'new' | null

function editing<T>(target: EditTarget<T>): T | undefined {
  return target === null || target === 'new' ? undefined : target
}

/* Settings holds more than its single nav line suggests: issuing entities,
 * numbering schemes, the reusable disclaimer and not-included templates, and
 * the direction catalogue a case leg is filled from. Super-admin only. */

const TABS = [
  { key: 'entities', labelKey: 'settings.tabs.entities' },
  { key: 'numbering', labelKey: 'settings.tabs.numbering' },
  { key: 'templates', labelKey: 'settings.tabs.templates' },
  { key: 'directions', labelKey: 'settings.tabs.directions' },
  { key: 'access', labelKey: 'settings.tabs.access' },
] as const

type TabKey = (typeof TABS)[number]['key']

function EntitiesPanel() {
  const { t } = useTranslation()
  const { data, isPending, isError, error, refetch } = useIssuingEntities()
  const [target, setTarget] = useState<EditTarget<IssuingEntity>>(null)

  const create = useCreateEntity()
  const update = useUpdateEntity()
  const setActive = useSetEntityActive()

  if (isPending) return <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  const record = editing(target)

  const submit = (values: EntityDraft) => {
    const payload = { ...values, logoVariant: record?.logoVariant ?? '' }
    if (record) {
      update.mutate({ id: record.id, payload }, { onSuccess: () => setTarget(null) })
      return
    }
    create.mutate(payload, { onSuccess: () => setTarget(null) })
  }

  return (
    <div className="grid gap-3.5 lg:grid-cols-2">
      {/* A refused change must say so where it was triggered — a duplicate name
          or an office another entity already claims both answer 409. */}
      {[create, update, setActive].map((mutation, index) =>
        mutation.error ? (
          <div key={index} className="lg:col-span-2">
            <ErrorState error={mutation.error} onRetry={() => mutation.reset()} />
          </div>
        ) : null,
      )}

      {data.map((entity) => (
        <Card key={entity.id}>
          <CardHeader
            action={
              <div className="flex gap-1.5">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setTarget(entity)}
                >
                  {t('common.actions.edit')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={setActive.isPending}
                  onClick={() =>
                    setActive.mutate({ id: entity.id, isActive: !entity.isActive })
                  }
                >
                  {entity.isActive
                    ? t('settings.entities.deactivate')
                    : t('settings.entities.activate')}
                </Button>
              </div>
            }
          >
            <CardTitle>{entity.name}</CardTitle>
          </CardHeader>
          <CardBody className="flex flex-col gap-3 pt-2">
            {/* Branding, bank details and the legal footer resolve from the
                issuing entity rather than being typed (FR-64) — that is what
                lets the two offices issue documents that differ correctly. */}
            <EntityLogoField entity={entity} />

            <div className="flex flex-wrap items-center gap-1.5">
              {/* The codes a numbering pattern expands, shown as the tokens
                  they stand in for so the two screens read as one system. */}
              <Tag>{`{INITIALS} ${entity.initials}`}</Tag>
              <Tag>{`{OFFICE} ${entity.officeCode}`}</Tag>
              {entity.defaultOffice ? (
                <Tag tone="brand">{entity.defaultOffice}</Tag>
              ) : null}
              {!entity.isActive ? <Tag>{t('settings.entities.inactive')}</Tag> : null}
            </div>

            <dl className="flex flex-col">
              {[
                [t('settings.entities.legalFooter'), entity.legalFooter],
                [t('settings.entities.bankDetails'), entity.bankDetails],
                [t('settings.entities.emailSignature'), entity.emailSignature],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="border-line-hair flex flex-col gap-0.5 border-b py-2 last:border-b-0"
                >
                  <dt className="asa-eyebrow">{label}</dt>
                  <dd className="text-text-body text-[12.5px] leading-snug font-light">
                    {/* An unset footer or bank block is a configuration gap that
                        reaches a client's paper, so it says so rather than
                        rendering an empty line nobody notices. */}
                    {value || (
                      <span className="text-text-muted italic">
                        {t('settings.entities.notSet')}
                      </span>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </CardBody>
        </Card>
      ))}

      <EntityFormSheet
        open={target !== null}
        onClose={() => setTarget(null)}
        record={record}
        pending={create.isPending || update.isPending}
        onSubmit={submit}
      />
    </div>
  )
}

function NumberingPanel() {
  const { t } = useTranslation()
  const { data, isPending, isError, error, refetch } = useNumberingSchemes()
  const [target, setTarget] = useState<NumberingScheme | null>(null)

  const save = useSetNumberingScheme()
  const preview = usePreviewNumbering()

  if (isPending) return <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  /* The preview comes from the server as the pattern is typed, so what the
     founder sees is what would actually be minted — including the real counter
     value. A rejected pattern surfaces as a translated message under the
     example rather than as a thrown error. */
  const previewFor = (pattern: string) => {
    if (!target) return
    preview.mutate({
      entityId: target.entityId,
      documentKind: target.documentKind,
      pattern,
    })
  }

  const previewError =
    preview.error instanceof ApiError
      ? t(patternMessageKey(preview.error))
      : undefined

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.numbering.title')}</CardTitle>
      </CardHeader>

      {save.error ? (
        <div className="px-3.5 pb-2">
          <ErrorState error={save.error} onRetry={() => save.reset()} />
        </div>
      ) : null}

      <div className="asa-scroll overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-line border-b">
              <th scope="col" className="asa-eyebrow h-9 pr-3 pl-[18px]">{t('settings.numbering.entity')}</th>
              <th scope="col" className="asa-eyebrow h-9 px-3">{t('settings.numbering.document')}</th>
              <th scope="col" className="asa-eyebrow h-9 px-3">{t('settings.numbering.pattern')}</th>
              <th scope="col" className="asa-eyebrow h-9 px-3">{t('settings.numbering.liveExample')}</th>
              <th scope="col" className="asa-eyebrow h-9 pr-[18px] pl-3 text-right">{t('settings.numbering.next')}</th>
            </tr>
          </thead>
          <tbody>
            {/* Every (entity, kind) pair, configured or defaulted. A screen
                listing only what is already set cannot be used to set the rest,
                and the defaults are in force either way (FR-68). */}
            {data.map((scheme) => (
              <tr
                key={`${scheme.entityId}-${scheme.documentKind}`}
                className="border-line-hair hover:bg-surface-hover h-[38px] cursor-pointer border-b transition-colors last:border-b-0"
                onClick={() => {
                  preview.reset()
                  setTarget(scheme)
                }}
                tabIndex={0}
                aria-label={t('settings.numbering.edit')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    preview.reset()
                    setTarget(scheme)
                  }
                }}
              >
                <td className="text-text-body pr-3 pl-[18px] text-[13px]">
                  {scheme.entityName}
                </td>
                <td className="px-3">
                  <Tag>{t(`settings.numbering.kinds.${scheme.documentKind}`)}</Tag>
                </td>
                <td className="text-text-body px-3 font-mono text-[12.5px]">
                  {scheme.pattern}
                  {!scheme.configured ? (
                    <span className="text-text-muted pl-2 font-sans text-[11.5px]">
                      {t('settings.numbering.usingDefault')}
                    </span>
                  ) : null}
                </td>
                <td className="text-text-heading px-3 font-mono text-[12.5px] font-semibold">
                  {scheme.example || '—'}
                </td>
                <td className="text-text-body pr-[18px] pl-3 text-right font-mono text-[12.5px]">
                  {scheme.nextValue}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NumberingFormSheet
        open={target !== null}
        onClose={() => {
          setTarget(null)
          preview.reset()
        }}
        record={target ?? undefined}
        pending={save.isPending}
        example={preview.data?.example ?? target?.example}
        exampleError={previewError}
        onPatternChange={previewFor}
        onSubmit={(values) => {
          if (!target) return
          save.mutate(
            {
              entityId: target.entityId,
              documentKind: target.documentKind,
              pattern: values.pattern,
            },
            {
              onSuccess: () => {
                setTarget(null)
                preview.reset()
              },
            },
          )
        }}
      />
    </Card>
  )
}

/** Maps the server's pattern field-error code to its message key.
 *
 * The server sends a stable token rather than a sentence, because the interface
 * is bilingual and a server-side English string would render untranslated in
 * the Russian UI (AD-27). */
function patternMessageKey(error: ApiError): ParseKeys {
  const code = error.problem.errors?.pattern?.[0]
  switch (code) {
    case 'missing-seq':
      return 'settings.numbering.errors.missingSeq'
    case 'unknown-token':
      return 'settings.numbering.errors.unknownToken'
    case 'seq-too-wide':
      return 'settings.numbering.errors.seqTooWide'
    default:
      return 'settings.numbering.errors.invalid'
  }
}

function TemplatesPanel() {
  const { t } = useTranslation()
  const { data, isPending, isError, error, refetch } = useDocumentTemplates()
  const [target, setTarget] = useState<EditTarget<DocumentTemplate>>(null)
  const [kind, setKind] = useState<DocumentTemplate['kind']>('not-included')

  /* Story 2.9 AC 2. Until these three landed, this panel's form sheet took
     `onSubmit={() => setTarget(null)}` — it validated a draft and threw it away,
     closing the dialog as though it had saved. That is the fourth appearance of
     that exact shape in this codebase (deferred-work.md records the
     case-participant sheet and the money page's add-cost modal), and it is the
     one bug class a reader cannot see from the screen. */
  const create = useCreateTemplate()
  const update = useUpdateTemplate()
  const remove = useDeleteTemplate()

  if (isPending) return <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  const record = editing(target)
  const pending = create.isPending || update.isPending

  /* One card per kind the server accepts, in the order the offer prints the
     printed three. `cost-item` is the fourth and is here for a reason nothing
     else on this screen shares: migration 0042 seeds seven charge-line presets
     and nothing ever re-seeds them, so this panel is the only place they can be
     corrected — and MarginPanel's hint sends the reader here to do it. */
  const groups = [
    { kind: 'included' as const, title: t('settings.templates.included') },
    { kind: 'not-included' as const, title: t('settings.templates.notIncluded') },
    { kind: 'disclaimer' as const, title: t('settings.templates.disclaimers') },
    {
      kind: 'cost-item' as const,
      title: t('settings.templates.costItems'),
      hint: t('settings.templates.costItemsHint'),
    },
  ]

  return (
    <div className="grid gap-3.5 lg:grid-cols-2">
      {/* A refused save or delete must say so where it was triggered: there is
          no global mutation error handler, so an unread `.error` is a silent
          refusal. The same shape EntitiesPanel above uses. */}
      {[create, update, remove].map((mutation, index) =>
        mutation.error ? (
          <div key={index} className="lg:col-span-2">
            <ErrorState error={mutation.error} onRetry={() => mutation.reset()} />
          </div>
        ) : null,
      )}

      {groups.map((group) => {
        const rows = data.filter((item) => item.kind === group.kind)
        return (
          <Card key={group.kind}>
            <CardHeader
              action={
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft={Plus}
                  onClick={() => {
                    setKind(group.kind)
                    setTarget('new')
                  }}
                >
                  {t('common.actions.add')}
                </Button>
              }
            >
              <CardTitle>{group.title}</CardTitle>
              {/* Only the presets carry one: the other three are text on an
                  offer and their heading says so, while a charge-line preset is
                  the one entry here that never prints and whose reader would
                  otherwise reasonably expect it to. */}
              {group.hint ? (
                <p className="text-text-muted text-[12.5px] leading-snug font-light">
                  {group.hint}
                </p>
              ) : null}
            </CardHeader>
            <ul className="flex flex-col px-[18px] pb-3">
              {rows.map((template, index) => (
                <li
                  key={template.id}
                  className={cn(
                    index !== rows.length - 1 && 'border-line-hair border-b',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setKind(template.kind)
                      setTarget(template)
                    }}
                    className="hover:bg-surface-hover -mx-2 flex w-[calc(100%+1rem)] cursor-pointer flex-col gap-1 rounded-md px-2 py-2.5 text-left transition-colors"
                  >
                    <span className="text-text-heading text-[13px]">
                      {template.label}
                    </span>
                    <span className="text-text-muted text-[12px] leading-snug font-light">
                      {template.body}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        )
      })}

      <TemplateFormSheet
        open={target !== null}
        onClose={() => setTarget(null)}
        record={record}
        kind={kind}
        pending={pending}
        onRemove={
          /* Only an existing template can be removed, and a hard delete is
             correct here for the reason 0033 gives: the body was COPIED onto
             every quote that used it, so nothing historical references this row.
             That is the payoff of the copy, and it is why this is a delete
             rather than the deactivation every directory record gets. */
          record
            ? () =>
                remove.mutate(record.id, { onSuccess: () => setTarget(null) })
            : undefined
        }
        removePending={remove.isPending}
        onSubmit={(values) => {
          const payload = {
            // Company-wide. Per-entity templates are a shape the server accepts
            // and no screen offers yet; sending null rather than omitting the
            // key keeps "no entity" explicit instead of defaulted.
            entityId: null,
            kind: values.kind,
            label: values.label,
            body: values.body,
          }
          if (record) {
            update.mutate(
              { id: record.id, payload },
              { onSuccess: () => setTarget(null) },
            )
            return
          }
          create.mutate(payload, { onSuccess: () => setTarget(null) })
        }}
      />
    </div>
  )
}

/** The route a direction describes, as the desk says it out loud.
 *
 *  Names only. The countries are on the record and deliberately not in this
 *  line: «Хоргос → Алматы → Сарахс → Ашхабад» is what somebody recognises at a
 *  glance, and interleaving four flags or four codes into it turns a sentence
 *  into a table nobody reads on the way past. */
function routeLine(direction: DirectionTemplate): string {
  return direction.points.map((point) => point.name).join(' → ')
}

/** The normative end-to-end duration: every transition summed.
 *
 *  The first point contributes nothing by construction — it has no inbound
 *  transition — so this is the time from arriving nowhere to arriving at the
 *  last point, which is the number the desk quotes. */
function totalDays(direction: DirectionTemplate): number {
  return direction.points.reduce((sum, point) => sum + (point.daysFromPrevious ?? 0), 0)
}

function DirectionsPanel() {
  const { t } = useTranslation()
  const { data, isPending, isError, error, refetch } = useDirectionTemplates()
  const [target, setTarget] = useState<EditTarget<DirectionTemplate>>(null)

  const create = useCreateDirection()
  const update = useUpdateDirection()
  const setActive = useSetDirectionActive()
  const remove = useDeleteDirection()

  if (isPending) return <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  const record = editing(target)

  /* The draft carries days as typed STRINGS and no positions at all; the wire
     shape carries numbers and lets the SERVER allocate positions from the
     order. Converting here rather than in the sheet keeps the sheet about the
     form and keeps one place that knows what the endpoint accepts. */
  const submit = (values: DirectionDraft) => {
    const payload = {
      name: values.name.trim(),
      // Editing a direction never flips it on or off — that is the row's own
      // control, and folding it in here would silently reactivate a retired
      // corridor because somebody fixed a typo in its name.
      active: record?.active ?? true,
      points: values.points.map((point, index) => ({
        name: point.name.trim(),
        country: point.country.trim().toUpperCase(),
        // The first point has no inbound transition; the server refuses a
        // number here as firmly as it refuses its absence anywhere else.
        daysFromPrevious: index === 0 ? null : Number(point.days.trim()),
      })),
    }
    if (record) {
      update.mutate({ id: record.id, payload }, { onSuccess: () => setTarget(null) })
      return
    }
    create.mutate(payload, { onSuccess: () => setTarget(null) })
  }

  return (
    <Card>
      <CardHeader
        action={
          <Button
            variant="secondary"
            size="sm"
            iconLeft={Plus}
            onClick={() => setTarget('new')}
          >
            {t('settings.directions.new')}
          </Button>
        }
      >
        <CardTitle>{t('settings.directions.title')}</CardTitle>
        {/* The copy-on-apply rule, stated on the screen rather than only in the
            code: a Super-admin correcting a corridor has to know that no case
            already running will move because of it. */}
        <p className="text-text-muted text-[12.5px] leading-snug font-light">
          {t('settings.directions.subtitle')}
        </p>
      </CardHeader>

      <CardBody className="flex flex-col gap-3 pt-2">
        {/* A refused save, activation or delete must say so where it was
            triggered — there is no global mutation error handler, so an unread
            `.error` is a silent refusal. The shape the panels above use. */}
        {[create, update, setActive, remove].map((mutation, index) =>
          mutation.error ? (
            /* The control CLEARS the refusal and says so. It cannot re-run
               anything: these four mutations are armed with no variables here,
               and the write that failed was submitted from a sheet that has
               since closed. Calling that "Try again" promised a second attempt
               that never happened — and on a refused save it is the honest
               action anyway, because the correction is made in the sheet. */
            <ErrorState
              key={index}
              error={mutation.error}
              onRetry={() => mutation.reset()}
              retryLabel={t('common.actions.dismiss')}
            />
          ) : null,
        )}

        {data.length === 0 ? (
          <p className="text-text-muted text-[12.5px] leading-snug font-light">
            {t('settings.directions.empty')}
          </p>
        ) : null}

        <ul className="flex flex-col">
          {data.map((direction, index) => (
            <li
              key={direction.id}
              className={cn(
                'flex items-start gap-2 py-2.5',
                index !== data.length - 1 && 'border-line-hair border-b',
              )}
            >
              <button
                type="button"
                onClick={() => setTarget(direction)}
                className="hover:bg-surface-hover -mx-2 flex min-w-0 flex-1 cursor-pointer flex-col gap-1 rounded-md px-2 py-1 text-left transition-colors"
              >
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="text-text-heading text-[13px]">{direction.name}</span>
                  {/* Neutral rather than negative: a switched-off corridor is
                      not a fault, it is one that is not currently offered. */}
                  {!direction.active ? (
                    <Tag>{t('settings.directions.inactive')}</Tag>
                  ) : null}
                </span>
                <span className="text-text-muted text-[12px] leading-snug font-light">
                  {t('settings.directions.pointCount', { count: direction.points.length })}
                  {' · '}
                  {t('settings.directions.normDays', { count: totalDays(direction) })}
                  {' · '}
                  {routeLine(direction)}
                </span>
              </button>

              <Button
                variant="ghost"
                size="sm"
                className="shrink-0"
                disabled={setActive.isPending}
                onClick={() =>
                  setActive.mutate({ id: direction.id, active: !direction.active })
                }
              >
                {direction.active
                  ? t('settings.directions.deactivate')
                  : t('settings.directions.activate')}
              </Button>
            </li>
          ))}
        </ul>
      </CardBody>

      <DirectionFormSheet
        open={target !== null}
        onClose={() => setTarget(null)}
        record={record}
        pending={create.isPending || update.isPending}
        onRemove={
          record
            ? () => remove.mutate(record.id, { onSuccess: () => setTarget(null) })
            : undefined
        }
        removePending={remove.isPending}
        onSubmit={submit}
      />
    </Card>
  )
}

export function SettingsPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab')
  const tab: TabKey = TABS.some((t) => t.key === requested)
    ? (requested as TabKey)
    : 'entities'

  return (
    <>
      <PageHeader
        title={t('settings.title')}
        subtitle={t('settings.subtitle')}
      />

      <TabNav
        label={t('settings.sectionsLabel')}
        items={TABS}
        value={tab}
        onChange={(key) => {
          const next = new URLSearchParams(searchParams)
          next.set('tab', key)
          setSearchParams(next, { replace: true })
        }}
      />

      <TabPanel tabKey={tab}>
        {tab === 'entities' ? <EntitiesPanel /> : null}
        {tab === 'numbering' ? <NumberingPanel /> : null}
        {/* Templates and thresholds share the tab, because Story 2.9 owns both
            and a founder setting up disclaimers is the same person setting up
            the reminder days. */}
        {tab === 'templates' ? (
          <div className="space-y-4">
            <TemplatesPanel />
            <ThresholdsPanel />
          </div>
        ) : null}
        {/* Its own tab rather than a card under Templates: a direction is not
            wording on a document, it is the route a shipment takes, and it is
            the one settings surface Operations' picker reads from. */}
        {tab === 'directions' ? <DirectionsPanel /> : null}
        {/* The editable roles come FIRST and the §3 reference table second:
            "what should this role do" is now an answerable question, but "what
            do the five shipped roles do" is still what somebody asks first. */}
        {tab === 'access' ? (
          <div className="space-y-4">
            <RolesPanel />
            <AccessMatrixPanel />
          </div>
        ) : null}
      </TabPanel>
    </>
  )
}
